/*
 * Buybox "New 2"
 * Loaded by sections/main-product.liquid only when the section setting "Buybox design" is set to "New 2".
 */

/**
 * <bb2-delivery-date> – calculates the delivery window client side (Liquid output is cached, so "now" would be stale).
 * Orders after the cutoff hour or on weekends ship on the next working day (Mon–Fri). Delivery days count Mon–Sat.
 */
class Bb2DeliveryDate extends HTMLElement {
  connectedCallback() {
    const minDays = parseInt(this.getAttribute('min-days'), 10) || 0;
    const maxDays = Math.max(parseInt(this.getAttribute('max-days'), 10) || 0, minDays);
    const cutoffHour = parseInt(this.getAttribute('cutoff-hour'), 10);
    const template = this.getAttribute('template') || '';

    const shipDate = this.getShipDate(new Date(), cutoffHour);
    const start = this.addDeliveryDays(shipDate, minDays);
    const end = this.addDeliveryDays(shipDate, maxDays);

    this.textContent = ' ' + template.replace('[start]', this.format(start)).replace('[end]', this.format(end));
  }

  getShipDate(now, cutoffHour) {
    const date = new Date(now);
    const isWorkingDay = (day) => day >= 1 && day <= 5;

    if (!isWorkingDay(date.getDay()) || (!isNaN(cutoffHour) && date.getHours() >= cutoffHour)) {
      do {
        date.setDate(date.getDate() + 1);
      } while (!isWorkingDay(date.getDay()));
    }

    return date;
  }

  addDeliveryDays(date, days) {
    const result = new Date(date);
    let remaining = days;

    while (remaining > 0) {
      result.setDate(result.getDate() + 1);

      if (result.getDay() !== 0) {
        remaining--;
      }
    }

    return result;
  }

  format(date) {
    const locale = this.getAttribute('locale') || document.documentElement.lang || 'de';
    // "Mi." as in the design: browsers differ in whether the short German weekday has a dot, so normalise it
    let weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date).replace(/\.$/, '');
    if (locale.toLowerCase().startsWith('de')) weekday += '.';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');

    return `${weekday} ${day}.${month}`;
  }
}

/**
 * <bb2-fit-text> – reduces the font size until the text fits on one line (the buybox column can be narrow on
 * tablets and small desktops too). When the minimum size
 * ("min-size" attribute, in px) is reached and it still does not fit, the text wraps with that size instead.
 */
class Bb2FitText extends HTMLElement {
  connectedCallback() {
    this.fit = this.fit.bind(this);
    this.resizeObserver = new ResizeObserver(() => this.fit());
    this.resizeObserver.observe(this.parentElement || this);
    document.fonts?.ready.then(this.fit);
    this.fit();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  fit() {
    this.style.removeProperty('font-size');
    this.style.removeProperty('line-height');
    this.classList.remove('is-wrapping');

    if (this.clientWidth === 0 || this.scrollWidth <= this.clientWidth) {
      return;
    }

    const minSize = parseFloat(this.getAttribute('min-size')) || 12;
    const computedStyle = getComputedStyle(this);
    const baseSize = parseFloat(computedStyle.fontSize);
    const lineHeightRatio = (parseFloat(computedStyle.lineHeight) || baseSize * 1.2) / baseSize;
    const applySize = (fontSize) => {
      this.style.fontSize = `${fontSize}px`;
      this.style.lineHeight = `${Math.round(fontSize * lineHeightRatio * 10) / 10}px`;
    };

    let size = Math.max(minSize, Math.floor(baseSize * (this.clientWidth / this.scrollWidth) * 2) / 2);
    applySize(size);

    // The ratio is only an estimate (letter widths do not scale perfectly), so fine tune in 0.5px steps
    while (this.scrollWidth > this.clientWidth && size > minSize) {
      size = Math.max(minSize, size - 0.5);
      applySize(size);
    }

    if (this.scrollWidth > this.clientWidth) {
      this.classList.add('is-wrapping');
    }
  }
}

/**
 * <bb2-variant-block> – the theme only re-renders a fixed list of block types when the variant changes. This element
 * listens to the same event and swaps its own content with the freshly rendered one.
 */
class Bb2VariantBlock extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const form = document.forms[this.getAttribute('form')];

    form?.addEventListener('product:rerender', (event) => {
      if (event.detail.productChange) {
        return; // The whole product info is replaced by the theme in that case
      }

      const matchingElement = event.detail.htmlFragment.querySelector(`#${CSS.escape(this.id)}`);

      if (matchingElement) {
        this.replaceChildren(...document.importNode(matchingElement, true).childNodes);
      }
    }, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }
}

/**
 * Empty blocks & separators
 * - App and custom liquid blocks keep a small padding so they do not stick to their neighbours. When a block renders
 *   nothing (an app without output for this product or visitor), that padding would only create an empty gap, so the
 *   block gets the "is-empty" class.
 * - A separator block with nothing visible between it and the next separator (or the end) would show up as a double
 *   line, e.g. on a product without variants, bundle builder and subscription widget. It gets "is-redundant".
 * Rendered heights are watched, as apps often render late.
 */
const Bb2EmptyBlocks = {
  paddedSelector: ':is([data-block-type="@app"], [data-block-type="liquid"])',
  frame: null,
  resizeObserver: new ResizeObserver(() => Bb2EmptyBlocks.schedule()),

  schedule() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.update());
  },

  update() {
    document.querySelectorAll('.product-info--new-2').forEach((productInfo) => {
      const items = Array.from(productInfo.querySelectorAll(':scope > .product-info__block-item, :scope > .product-info__block-group'));

      items.filter((item) => item.matches(this.paddedSelector)).forEach((item) => {
        const style = getComputedStyle(item);
        const contentHeight = item.getBoundingClientRect().height - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
        item.classList.toggle('is-empty', contentHeight < 1);
      });

      // Walk backwards: a separator is redundant when no visible block follows before the next visible separator
      let hasContentAfter = false;

      for (let index = items.length - 1; index >= 0; index--) {
        const item = items[index];

        if (item.dataset.blockType === 'separator') {
          item.classList.toggle('is-redundant', !hasContentAfter);
          hasContentAfter = false;
        } else if (item.getBoundingClientRect().height >= 1) {
          hasContentAfter = true;
        }
      }
    });
  },

  observe() {
    document.querySelectorAll('.product-info--new-2 > :is(.product-info__block-item, .product-info__block-group)').forEach((item) => {
      if (!item.hasAttribute('data-bb2-observed')) {
        item.setAttribute('data-bb2-observed', '');
        this.resizeObserver.observe(item);
      }
    });

    this.schedule();
  }
};

Bb2EmptyBlocks.observe();

// The theme replaces the product info when switching to another product (combined listings), so observe new blocks too
let bb2ObserveFrame = null;
new MutationObserver(() => {
  cancelAnimationFrame(bb2ObserveFrame);
  bb2ObserveFrame = requestAnimationFrame(() => Bb2EmptyBlocks.observe());
}).observe(document.body, { childList: true, subtree: true });

/**
 * Video popup – any element with [data-bb2-video="template-id"] opens the video stored in <template id="template-id">.
 * Uses event delegation so it keeps working after the theme re-renders the product info.
 */
const Bb2VideoModal = {
  dialog: null,

  getDialog() {
    if (this.dialog) {
      return this.dialog;
    }

    const closeLabel = window.bb2Strings?.close || 'Close';
    const dialog = document.createElement('dialog');
    dialog.className = 'bb2-modal';
    dialog.innerHTML = `
      <div class="bb2-modal__inner">
        <button type="button" class="bb2-modal__close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <div class="bb2-modal__content"></div>
      </div>`;

    const closeButton = dialog.querySelector('.bb2-modal__close');
    closeButton.setAttribute('aria-label', closeLabel);
    closeButton.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        dialog.close();
      }
    });
    dialog.addEventListener('close', () => {
      dialog.querySelector('.bb2-modal__content').replaceChildren();
      document.documentElement.style.removeProperty('overflow');
    });

    document.body.appendChild(dialog);
    this.dialog = dialog;

    return dialog;
  },

  open(templateId) {
    const template = document.getElementById(templateId);

    if (!template) {
      return;
    }

    const dialog = this.getDialog();
    dialog.querySelector('.bb2-modal__content').replaceChildren(template.content.cloneNode(true));
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();

    const video = dialog.querySelector('video');
    video?.play().catch(() => {
      // Autoplay with sound can be blocked – the controls stay available
    });
  }
};

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-bb2-video]');

  if (trigger) {
    event.preventDefault();
    Bb2VideoModal.open(trigger.getAttribute('data-bb2-video'));
  }
});

if (!customElements.get('bb2-delivery-date')) {
  customElements.define('bb2-delivery-date', Bb2DeliveryDate);
}

if (!customElements.get('bb2-fit-text')) {
  customElements.define('bb2-fit-text', Bb2FitText);
}

if (!customElements.get('bb2-variant-block')) {
  customElements.define('bb2-variant-block', Bb2VariantBlock);
}
